import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { etudiantsAPI } from '../services/api';
import {
    initiales, badgeStatut, badgeRegime, dateFr, ouTiret,
    libelleChamp, enumererChamps
} from '../services/etudiantsAffichage';
import PhotoEtudiant from './PhotoEtudiant';

/**
 * ONGLET 1 — LA FICHE INDIVIDUELLE
 * ================================
 * Spec : doc/modules/etudiants.md §Onglet 1 « Fiche individuelle — principe d'édition »
 * Maquette : `etudiants_maquette_v5.html`, onglet 1, panneau de droite.
 * Lot 1.3 (la lecture) puis **1.3 bis** (l'écriture, les cadenas cliquables,
 * « Enregistrer » et « Contacter »), puis **1.4** (les DEUX rangs venus de SCODOC —
 * 17ᵉ champ modifiable, 15ᵉ cadenas).
 *
 * ── LES TROIS SOURCES, ET CE QUE LE CADENAS PROTÈGE ──────────────────────────
 * `APOGEE` : la valeur vient de l'import APOGEE. Modifiable à la main, et cette
 *            correction se VERROUILLE — l'import suivant ne l'écrase plus.
 * `SCODOC` : la valeur vient d'un import SCODOC. Même règle, autre import — et
 *            l'infobulle du cadenas nomme donc SCODOC, pas APOGEE. DEUX champs, et
 *            ils ne se confondent pas (mesuré sur les fichiers réels) :
 *              · **Rang admission** — export de RECRUTEMENT, 11 → 210 : le rang sur
 *                tous les candidats classés ;
 *              · **Rang promo** — RELEVÉ DE NOTES, 1 → 41 : le rang dans la promotion,
 *                AU SEMESTRE. Le semestre s'affiche à côté, et ce champ ne se saisit
 *                qu'après l'import de son relevé (le semestre vient de là).
 * `APP`    : saisie ou calculée par l'application. Aucun import n'y touche, donc elle
 *            n'a pas besoin d'être protégée.
 *
 * La liste des champs verrouillables ne vit pas ici : elle est renvoyée par le serveur
 * (`champs_verrouillables`), qui la DÉRIVE de la liste blanche de l'import. Une copie
 * dans cet écran aurait fini par promettre un cadenas sur un champ que l'import écrase
 * quand même.
 *
 * ── LA RÈGLE DU CADENAS, TELLE QU'ELLE EST LIVRÉE ────────────────────────────
 * 1. Modifier un champ APOGEE le VERROUILLE, automatiquement. C'est ce que dit la
 *    spec, et c'est ce que la légende de la maquette annonce à l'écran.
 * 2. Le verrou se pose À LA FRAPPE, pas à l'enregistrement : sinon on ne verrait la
 *    conséquence de son geste qu'après l'avoir validé.
 * 3. Un CLIC sur le cadenas fait autorité sur la règle automatique. Corriger un champ
 *    *et* rouvrir son cadenas est donc permis : la valeur est enregistrée, et le
 *    prochain import la reprendra. C'est le sens même de « déverrouiller ».
 * 4. Déverrouiller ne RESTAURE PAS la valeur d'APOGEE : elle reste telle quelle
 *    jusqu'au prochain import. L'infobulle le dit, sinon le geste promettrait ce
 *    qu'il ne fait pas.
 *
 * ── CE QUI EST MODIFIABLE, ET QUAND ──────────────────────────────────────────
 * Les champs de la PERSONNE (identité, contact, bac, référent) le sont toujours : un
 * nom n'appartient à aucune année. Ceux de l'INSCRIPTION (statut, boursier,
 * redoublant, les trois rangs) ne le sont que sur l'année ACTIVE — même raisonnement que
 * l'écart E25 pour l'import, et pour la même raison : réécrire le statut d'une
 * promotion close serait réécrire une histoire que d'autres écrans relisent. Décidé
 * avec Bertrand le 2026-08-26.
 *
 * ── ÉCARTS DE MAQUETTE, TOUS ÉCRITS DANS LA SPEC ─────────────────────────────
 * E21 — ni « Adresse » ni « Fixe » : ces colonnes n'existent pas en base (§0.6, RGPD).
 * E31 — le bac tient sur trois lignes et trois cadenas, pas une.
 * E32 — pas de cadenas sur « Statut », et son tag est `APP` : l'import ne le pose
 *       qu'à la CRÉATION. Un cadenas qui ne protège de rien est un mensonge à l'écran.
 * E33 — « Sexe » est ajouté, que la maquette omet.
 * E38 — la maquette dessine TOUS les champs en saisie libre, y compris ceux dont le
 *       domaine est fermé. Trois deviennent des menus ou des champs typés : le sexe
 *       (la colonne fait un seul caractère, « Féminin » n'y entrerait pas), la mention
 *       (P/AB/B/TB, mesuré 146/146) et la date de naissance (un calendrier plutôt
 *       qu'une chaîne à relire — relire « 02/12/2007 » aurait été le devineur que le
 *       §0.8 interdit).
 */

// ═══════════════════════════════════════════════════════════════════════════════
// LE FORMULAIRE — les valeurs de l'écran, sous la seule forme qu'un champ HTML porte
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * ⚠️ Ces deux listes disent quels champs l'écran DESSINE, et rien d'autre. Elles ne
 * remplacent pas `champs_verrouillables`, qui vient du serveur et décide où poser un
 * cadenas : un champ peut être dessiné sans être verrouillable (le statut, le
 * référent). Les confondre, c'est promettre un cadenas sur un champ que l'import
 * écrase quand même.
 */
const CHAMPS_FORMULAIRE_PERSONNE = [
    'nom', 'prenom', 'sexe', 'date_naissance', 'telephone', 'email',
    'type_bac', 'mention_bac', 'annee_bac', 'cursus', 'enseignant_referent_id'
];
const CHAMPS_FORMULAIRE_INSCRIPTION = [
    'statut', 'boursier', 'redoublant', 'classement_admission',
    // Lot 1.4. DEUX champs venus de SCODOC et non d'APOGEE : les seuls de la fiche
    // dont le cadenas fait taire l'AUTRE import, d'où leur tag et leur infobulle.
    // Ce sont aussi deux rangs de nature différente — cf. la section « Suivi
    // pédagogique » du rendu.
    'classement_recrutement', 'rang_promotion',
    // 2026-10-22 (demande de Bertrand) : la formation et le régime se corrigent ici ; leur cadenas les protège du
    // prochain import APOGEE (pour l'année de la fiche).
    'sous_niveau_id', 'regime_inscription',
    // 2026-10-29 (E.4) : le parcours de l'année (vide = tronc commun), cadenas de l'année comme la formation.
    'parcours_pn_id'
];

const texteOuVide = (v) => (v === null || v === undefined ? '' : String(v));
/** `null` n'est PAS `false` : « inconnu » se dit, il ne se devine pas. */
const ouiNonOuVide = (v) => (v === true ? 'oui' : v === false ? 'non' : '');
/** La date arrive en ISO du serveur ; `input[type=date]` attend `AAAA-MM-JJ`. */
const dateISO = (v) => (v ? String(v).slice(0, 10) : '');

/** L'état du formulaire tel que la base le dit — le point de comparaison du « modifié ». */
function formulaireDepuis(fiche) {
    const e = fiche?.etudiant || {};
    const i = fiche?.inscription || null;
    return {
        personne: {
            nom: texteOuVide(e.nom),
            prenom: texteOuVide(e.prenom),
            sexe: texteOuVide(e.sexe),
            date_naissance: dateISO(e.date_naissance),
            telephone: texteOuVide(e.telephone),
            email: texteOuVide(e.email),
            type_bac: texteOuVide(e.type_bac),
            mention_bac: texteOuVide(e.mention_bac),
            annee_bac: texteOuVide(e.annee_bac),
            cursus: texteOuVide(e.cursus),
            enseignant_referent_id: texteOuVide(e.enseignant_referent_id)
        },
        inscription: {
            statut: texteOuVide(i?.statut),
            boursier: ouiNonOuVide(i?.boursier),
            redoublant: ouiNonOuVide(i?.redoublant),
            classement_admission: texteOuVide(i?.classement_admission),
            classement_recrutement: texteOuVide(i?.classement_recrutement),
            rang_promotion: texteOuVide(i?.rang_promotion),
            sous_niveau_id: texteOuVide(i?.sous_niveau_id),
            regime_inscription: texteOuVide(i?.regime_inscription),
            parcours_pn_id: texteOuVide(i?.parcours_pn_id)
        }
    };
}

/** Le formulaire retraduit pour le serveur : une case vide devient `null`, jamais `""`. */
function versServeur(form) {
    const vide = (t) => (String(t).trim() === '' ? null : String(t).trim());
    const entier = (t) => (String(t).trim() === '' ? null : Number(t));
    const booleen = (t) => (t === 'oui' ? true : t === 'non' ? false : null);
    const p = form.personne;
    const i = form.inscription;
    return {
        personne: {
            nom: vide(p.nom), prenom: vide(p.prenom), sexe: vide(p.sexe),
            date_naissance: vide(p.date_naissance), telephone: vide(p.telephone),
            email: vide(p.email), type_bac: vide(p.type_bac),
            mention_bac: vide(p.mention_bac), annee_bac: entier(p.annee_bac),
            cursus: vide(p.cursus), enseignant_referent_id: entier(p.enseignant_referent_id)
        },
        inscription: {
            statut: vide(i.statut), boursier: booleen(i.boursier),
            redoublant: booleen(i.redoublant), classement_admission: entier(i.classement_admission),
            classement_recrutement: entier(i.classement_recrutement),
            rang_promotion: entier(i.rang_promotion),
            sous_niveau_id: entier(i.sous_niveau_id),
            regime_inscription: vide(i.regime_inscription),
            parcours_pn_id: entier(i.parcours_pn_id)
        }
    };
}

// ═══════════════════════════════════════════════════════════════════════════════
// LES BRIQUES D'UNE LIGNE
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Le cadenas. Deux états, comme la maquette : fermé (rose) quand le champ est protégé,
 * ouvert (gris) sinon. Cliquable dès que la fiche l'est — c'est le lot 1.3 bis.
 */
function Cadenas({ verrouille, actif, onBasculer, raisonInactif, source }) {
    // ⚠️ L'infobulle NOMME l'import concerné, et depuis le lot 1.4 ce n'est plus
    // toujours APOGEE : le classement de recrutement vient de SCODOC. Dire « le
    // prochain import APOGEE ne l'écrasera pas » sur ce champ serait faux deux fois —
    // APOGEE ne l'écrit jamais, et c'est de SCODOC que l'utilisateur veut se protéger.
    // 2026-10-29 (E.4) : une valeur saisie à la main peut être reprise par l'UN OU L'AUTRE import.
    const quelImport = source === 'scodoc' ? 'SCODOC' : source === 'manuel' ? '(APOGEE ou SCODOC)' : 'APOGEE';
    const infobulle = !actif
        ? raisonInactif
        : verrouille
            ? `Verrouillé : le prochain import ${quelImport} ne l’écrasera pas. Cliquer pour l’ouvrir — `
              + "la valeur affichée ne changera pas, mais le prochain import pourra la remplacer."
            : `Non verrouillé : le prochain import ${quelImport} peut remplacer cette valeur. `
              + "Cliquer pour la protéger.";
    return (
        <button
            type="button"
            disabled={!actif}
            onClick={actif ? onBasculer : undefined}
            className={'etu-cadenas' + (verrouille ? '' : ' etu-cadenas-ouvert')}
            title={infobulle}
            aria-pressed={!!verrouille}
            aria-label={verrouille ? 'Champ verrouillé' : 'Champ non verrouillé'}
        >
            <i className={'fa-solid ' + (verrouille ? 'fa-lock' : 'fa-lock-open')} />
        </button>
    );
}

/**
 * Une ligne de fiche. Le rendu est le même partout : c'est ce qui fait la grille.
 * Le libellé vient de `LIBELLES_CHAMPS` — un seul endroit, partagé avec le bandeau
 * d'enregistrement qui doit nommer les champs écrits à partir de leurs clés.
 */
function Ligne({ cle, source, note, verrou, verrouActif, onVerrou, raisonVerrou, children }) {
    return (
        <div className="etu-fiche-ligne">
            <span className="etu-fiche-libelle">
                {libelleChamp(cle)}
                {source === 'apogee' && <span className="etu-tag-apogee" title="Valeur venue de l’import APOGEE">APOGEE</span>}
                {source === 'scodoc' && <span className="etu-tag-scodoc" title="Valeur venue d’un import SCODOC">SCODOC</span>}
                {source === 'manuel' && <span className="etu-tag-manuel" title="Valeur saisie sur cette fiche">MANUEL</span>}
                {source === 'app' && <span className="etu-tag-app">APP</span>}
            </span>
            <span className="etu-fiche-valeur">
                {children}
                {note && <span className="etu-fiche-note">{note}</span>}
                {verrou !== undefined && (
                    <Cadenas
                        verrouille={verrou}
                        actif={verrouActif}
                        onBasculer={onVerrou}
                        raisonInactif={raisonVerrou}
                        source={source}
                    />
                )}
            </span>
        </div>
    );
}

/** Champ en lecture pure : la forme d'un champ de saisie, sans l'être. */
function Lecture({ valeur, classe, large }) {
    return (
        <input
            type="text"
            readOnly
            value={valeur === null || valeur === undefined ? '' : String(valeur)}
            className={(classe || '') + (large ? ' etu-fiche-large' : '')}
            placeholder="Non renseigné"
            tabIndex={-1}
        />
    );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LA FICHE
// ═══════════════════════════════════════════════════════════════════════════════

function EtudiantFiche({
    etudiantId, anneeId, departementId, modifiable,
    onErreurPerimetre, onEnregistre, refActions
}) {
    const [chargement, setChargement] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [fiche, setFiche] = useState(null);
    const [form, setForm] = useState(null);
    /** Les cadenas que l'utilisateur a cliqués : ils l'emportent sur la règle automatique. */
    const [verrousManuels, setVerrousManuels] = useState({});
    const [bandeau, setBandeau] = useState(null);
    const [enregistrement, setEnregistrement] = useState(false);

    /**
     * ⚠️ LES DEUX RAPPELS DU PARENT PASSENT PAR UN REF, ET C'EST UN CORRECTIF.
     *
     * Trouvé à la relecture à l'image : la coquille les recrée à chaque rendu
     * (`onEnregistre={() => chargerListe()}`). Mis dans les dépendances de `charger`,
     * ils faisaient changer `charger` à chaque rafraîchissement de la liste — donc
     * RELIRE la fiche, donc **effacer la saisie en cours**. Une frappe dans la
     * recherche suffisait à perdre une correction commencée.
     *
     * Le parent enveloppe désormais ses rappels dans `useCallback`, mais la fiche ne
     * doit pas en dépendre pour être correcte : un ref rend le défaut impossible à
     * refaire depuis l'extérieur.
     */
    const refRappels = useRef({ onErreurPerimetre, onEnregistre });
    useEffect(() => { refRappels.current = { onErreurPerimetre, onEnregistre }; },
        [onErreurPerimetre, onEnregistre]);

    const charger = useCallback(async () => {
        if (!etudiantId || !departementId) { setFiche(null); setForm(null); return; }
        setChargement(true);
        setErreur(null);
        try {
            const { data } = await etudiantsAPI.getFiche(departementId, etudiantId, anneeId);
            setFiche(data);
            setForm(formulaireDepuis(data));
            setVerrousManuels({});
            setBandeau(null);
        } catch (e) {
            const message = e?.response?.data?.error || e?.message || 'Fiche illisible';
            setErreur(message);
            setFiche(null);
            setForm(null);
            // Un 403 de périmètre n'est pas l'affaire de la fiche : c'est la liste qui
            // affiche une ligne qu'elle n'aurait pas dû montrer. On le remonte.
            if (e?.response?.status === 403) refRappels.current.onErreurPerimetre?.(message);
        } finally {
            setChargement(false);
        }
    }, [etudiantId, anneeId, departementId]);

    useEffect(() => { charger(); }, [charger]);

    // ── Ce qui a bougé ────────────────────────────────────────────────────────
    const initial = useMemo(() => (fiche ? formulaireDepuis(fiche) : null), [fiche]);

    const aChange = useCallback((cle) => {
        if (!form || !initial) return false;
        if (CHAMPS_FORMULAIRE_PERSONNE.includes(cle)) {
            return form.personne[cle] !== initial.personne[cle];
        }
        if (CHAMPS_FORMULAIRE_INSCRIPTION.includes(cle)) {
            return form.inscription[cle] !== initial.inscription[cle];
        }
        return false;
    }, [form, initial]);

    const verrouillables = useMemo(
        () => fiche?.champs_verrouillables || [],
        [fiche]
    );
    const verrousBase = useMemo(
        () => fiche?.etudiant?.champs_verrouilles || {},
        [fiche]
    );

    /** L'état affiché d'un cadenas : le clic d'abord, sinon la base ou la frappe. */
    const verrouAffiche = useCallback((cle) => {
        if (cle in verrousManuels) return verrousManuels[cle];
        return verrousBase[cle] === true || aChange(cle);
    }, [verrousManuels, verrousBase, aChange]);

    const valeursChangees = useMemo(() => {
        if (!form || !initial) return [];
        return [...CHAMPS_FORMULAIRE_PERSONNE, ...CHAMPS_FORMULAIRE_INSCRIPTION].filter(aChange);
    }, [form, initial, aChange]);

    const verrousChanges = useMemo(
        () => verrouillables.filter((c) => verrouAffiche(c) !== (verrousBase[c] === true)),
        [verrouillables, verrouAffiche, verrousBase]
    );

    const modifie = valeursChangees.length > 0 || verrousChanges.length > 0;

    // ── Ce qui est modifiable, et pourquoi ────────────────────────────────────
    const anneeActive = fiche?.annee?.active === true;
    const inscription = fiche?.inscription || null;
    const personneModifiable = !!modifiable;
    // Les champs de l'inscription : année active seulement, et il faut une inscription.
    const inscriptionModifiable = !!modifiable && anneeActive && !!inscription;


    const raisonInscription = !modifiable
        ? 'Ton profil ne permet que la consultation de ce module.'
        : !anneeActive
            ? `Statut, boursier, redoublant et classement ne se modifient que sur l’année active — `
              + `la fiche est ouverte sur ${fiche?.annee?.libelle || 'une autre année'}.`
            : 'Aucune inscription pour cette année.';
    const raisonPersonne = 'Ton profil ne permet que la consultation de ce module.';

    /**
     * Le rang dans la promotion ne se saisit qu'APRÈS l'import de son relevé, parce
     * que son semestre vient de là et que la base refuse un rang sans semestre
     * (contrainte `ei_rang_promotion_date`, v39). Le serveur le refuse aussi, avec le
     * même message : l'écran ne fait que l'éviter plutôt que de laisser l'utilisateur
     * taper pour rien.
     *
     * ⚠️ CE BLOC EST ICI, APRÈS `raisonInscription`, ET PAS AILLEURS. Placé avant, il
     * la lisait dans sa ZONE MORTE TEMPORELLE : un `const` existe dès l'entrée dans la
     * portée mais n'est pas accessible avant sa ligne. Le code COMPILE sans un mot —
     * webpack et Babel n'y voient rien — et l'écran plante au premier rendu de la
     * fiche, sur « Cannot access 'raisonInscription' before initialization ». Trouvé à
     * la relecture À L'IMAGE, qui est le seul contrôle à exécuter vraiment l'écran.
     */
    const releveImporte = !!inscription?.rang_promotion_semestre;
    const rangPromoModifiable = inscriptionModifiable && releveImporte;
    const raisonRangPromo = !inscriptionModifiable
        ? raisonInscription
        : 'Ce rang porte sur un semestre, et le semestre vient du relevé de notes. '
          + 'Importe le relevé dans l’onglet Imports (colonne SCODOC), puis reviens le '
          + 'corriger si besoin — la correction sera protégée par son cadenas.';

    // ── Les gestes de saisie ──────────────────────────────────────────────────
    const changerPersonne = useCallback((cle, valeur) => {
        setBandeau(null);
        setForm((p) => ({ ...p, personne: { ...p.personne, [cle]: valeur } }));
    }, []);
    const changerInscription = useCallback((cle, valeur) => {
        setBandeau(null);
        setForm((p) => ({ ...p, inscription: { ...p.inscription, [cle]: valeur } }));
    }, []);
    const basculerVerrou = useCallback((cle) => {
        setBandeau(null);
        setVerrousManuels((p) => ({ ...p, [cle]: !verrouAffiche(cle) }));
    }, [verrouAffiche]);

    // ── L'enregistrement ──────────────────────────────────────────────────────
    const enregistrer = useCallback(async () => {
        if (!fiche || !form || !modifie || enregistrement) return false;
        setEnregistrement(true);
        setBandeau(null);
        try {
            const valeurs = versServeur(form);
            const carte = {};
            for (const cle of verrouillables) carte[cle] = verrouAffiche(cle);

            const { data } = await etudiantsAPI.saveFiche(
                departementId, fiche.etudiant.id, fiche.annee?.id,
                {
                    updated_at: fiche.etudiant.updated_at,
                    inscription_updated_at: inscription?.updated_at,
                    personne: valeurs.personne,
                    // Rien n'est envoyé quand l'inscription n'est pas modifiable : le
                    // serveur le refuserait de toute façon, mais un écran qui envoie ce
                    // qu'il ne peut pas écrire finit par le faire par erreur.
                    ...(inscriptionModifiable ? { inscription: valeurs.inscription } : {}),
                    champs_verrouilles: carte
                }
            );

            // La réponse porte la fiche RELUE : c'est elle qui fait foi, pas ce que
            // l'écran croyait avoir saisi.
            setFiche(data);
            setForm(formulaireDepuis(data));
            setVerrousManuels({});

            const b = data.bilan || {};
            const morceaux = [];
            if (b.champs_ecrits?.length) morceaux.push(`${enumererChamps(b.champs_ecrits)} enregistré${b.champs_ecrits.length > 1 ? 's' : ''}`);
            if (b.verrous_poses?.length) morceaux.push(`${b.verrous_poses.length} cadenas fermé${b.verrous_poses.length > 1 ? 's' : ''} (${enumererChamps(b.verrous_poses)})`);
            if (b.dossiers_stage_ouverts?.length) morceaux.push(`dossier de stage ouvert dans ${b.dossiers_stage_ouverts.length} processus de suivi`);
            if (b.verrous_retires?.length) morceaux.push(`${b.verrous_retires.length} cadenas ouvert${b.verrous_retires.length > 1 ? 's' : ''} (${enumererChamps(b.verrous_retires)})`);
            setBandeau({
                type: b.ecrit ? 'succes' : 'neutre',
                texte: b.ecrit
                    ? morceaux.join(' · ')
                    : "Rien à enregistrer : les valeurs de la fiche sont déjà celles de la base."
            });
            refRappels.current.onEnregistre?.();
            return true;
        } catch (e) {
            const statut = e?.response?.status;
            setBandeau({
                type: 'erreur',
                texte: e?.response?.data?.error || e?.message || 'Enregistrement refusé',
                detail: e?.response?.data?.detail,
                // Le 409 a une issue, et une seule : relire. Le bandeau la propose.
                rechargeable: statut === 409
            });
            return false;
        } finally {
            setEnregistrement(false);
        }
    }, [fiche, form, modifie, enregistrement, verrouillables, verrouAffiche,
        departementId, inscription, inscriptionModifiable]);

    // Le parent doit pouvoir demander « es-tu modifiée ? » et « enregistre » quand
    // l'utilisateur quitte la fiche : c'est la modale des modifications non
    // enregistrées, et elle vit là-haut parce qu'elle doit aussi couvrir le changement
    // de filtre et le changement d'onglet.
    useEffect(() => {
        if (refActions) refActions.current = { modifie, enregistrer };
        return () => { if (refActions) refActions.current = null; };
    }, [refActions, modifie, enregistrer]);

    // ── « Contacter » ─────────────────────────────────────────────────────────
    // `mailto:` sur l'e-mail institutionnel, le personnel en repli. Décidé le
    // 2026-08-24 : c'est ce que fait déjà le module Stage, ça marche sans dépendance,
    // et ça ne couple pas ce module au module Communication.
    const adresseContact = fiche?.etudiant?.email_institutionnel || fiche?.etudiant?.email || '';

    // ── DÉSACTIVER / RÉACTIVER (lot 1.6, écart E9) ───────────────────────────
    // ⚠️ Le soft delete est une règle métier du module (« pas de suppression
    // physique — `actif = false` »), et la maquette n'en dessinait AUCUN chemin.
    // Ce geste est donc conçu ici, et il est traité comme ce qu'il est : une
    // décision, pas un réglage.
    //
    // ⚠️ IL SUPPRIME LA PHOTO, et c'est irréversible. D'où une modale — jamais un
    // `confirm()` (règle 6) — qui NOMME la conséquence avant de la produire.
    const [demandeActif, setDemandeActif] = useState(null);   // true | false | null

    // ── 2026-10-22 : la fenêtre de confirmation d'une NOUVELLE FORMATION / d'un NOUVEAU RÉGIME ───────────────────
    // Elle récapitule « avant → après » et ce que ça change ailleurs (dossiers de stage, registre des apprentis,
    // certification) — jamais un `confirm()` (règle 6).
    const [confirmation, setConfirmation] = useState(null);   // { avant, apres, messages } | null
    const [lectureConsequences, setLectureConsequences] = useState(false);
    const changeInscriptionMapping = !!form && !!initial
        && (form.inscription.sous_niveau_id !== initial.inscription.sous_niveau_id
            || form.inscription.regime_inscription !== initial.inscription.regime_inscription);
    const demanderEnregistrement = async () => {
        if (!changeInscriptionMapping) { enregistrer(); return; }
        setLectureConsequences(true);
        try {
            const { data } = await etudiantsAPI.consequencesFiche(departementId, fiche.etudiant.id, fiche.annee?.id, {
                sous_niveau_id: form.inscription.sous_niveau_id || undefined,
                regime_inscription: form.inscription.regime_inscription || undefined
            });
            setConfirmation(data);
        } catch (e) {
            setBandeau({ type: 'erreur', texte: e?.response?.data?.error || 'Lecture des conséquences impossible', detail: e?.response?.data?.detail });
        } finally {
            setLectureConsequences(false);
        }
    };
    const [bascule, setBascule] = useState(false);

    const changerActif = async (versActif) => {
        setBascule(true);
        try {
            const r = await etudiantsAPI.changerActif(departementId, etudiantId, versActif);
            setDemandeActif(null);
            // ⚠️ On RECHARGE la fiche plutôt que de recoller l'état à la main : la
            // désactivation a effacé `photo_url`, et l'avatar doit repasser aux
            // initiales. Rafistoler un champ laisserait l'image morte à l'écran.
            await charger();
            // Par la RÉFÉRENCE, comme partout ailleurs ici : un rappel de parent
            // appelé directement finit dans les dépendances de quelque chose, et le
            // lot 1.3 bis a payé ce piège — la saisie en cours s'effaçait.
            refRappels.current.onEnregistre?.();
            return r.data;
        } catch (err) {
            setErreur(err.response?.data?.error || "Le changement d'état a échoué");
            setDemandeActif(null);
            return null;
        } finally {
            setBascule(false);
        }
    };

    // ── Rien de sélectionné ───────────────────────────────────────────────────
    if (!etudiantId) {
        return (
            <div className="etu-carte">
                <div className="etu-carte-entete">
                    <i className="fa-solid fa-id-card" />
                    Fiche individuelle
                </div>
                <div className="etu-vide">
                    <i className="fa-solid fa-hand-pointer" />
                    <div className="etu-vide-titre">Aucun étudiant sélectionné</div>
                    <p className="etu-vide-texte">
                        Clique une ligne de la liste : la fiche s'ouvre ici, en deux colonnes.
                        Chaque champ venu d'un import porte un cadenas — fermé, l'import suivant
                        ne l'écrase plus.
                    </p>
                </div>
            </div>
        );
    }

    if (chargement && !fiche) {
        return (
            <div className="etu-carte">
                <div className="etu-vide">
                    <i className="fa-solid fa-circle-notch etu-tourne" />
                    <div className="etu-vide-titre">Lecture de la fiche…</div>
                </div>
            </div>
        );
    }

    if (erreur) {
        return (
            <div className="etu-carte">
                <div className="etu-vide">
                    <i className="fa-solid fa-triangle-exclamation" />
                    <div className="etu-vide-titre">Cette fiche n'a pas pu être lue</div>
                    <p className="etu-vide-texte">{erreur}</p>
                </div>
            </div>
        );
    }

    if (!fiche || !form) return null;

    const e = fiche.etudiant;
    const ins = fiche.inscription;
    const st = badgeStatut(ins?.statut);
    const statutsMenu = fiche.statuts || [];
    const referent = (fiche.enseignants || []).find((x) => String(x.id) === form.personne.enseignant_referent_id);

    /**
     * 2026-10-29 (E.4, v98) — L'ORIGINE RÉELLE d'une valeur : celle que le serveur a enregistrée (`champs_sources` de la
     * personne et de l'inscription : apogee, scodoc, manuel). Inconnue (valeur antérieure à la v98) → l'étiquette d'avant.
     */
    const origines = { ...(e.champs_sources || {}), ...(ins?.champs_sources || {}) };
    const src = (cle, parDefaut) => origines[cle] || parDefaut;

    /** Le cadenas d'un champ, ou `undefined` s'il n'est pas verrouillable (contrat serveur). */
    const verrou = (cle) => (verrouillables.includes(cle) ? verrouAffiche(cle) : undefined);
    const verrouActif = (cle) => (CHAMPS_FORMULAIRE_INSCRIPTION.includes(cle)
        ? inscriptionModifiable : personneModifiable);
    const raisonVerrou = (cle) => (CHAMPS_FORMULAIRE_INSCRIPTION.includes(cle)
        ? raisonInscription : raisonPersonne);

    /** Un champ texte de la personne : la seule forme répétée quinze fois. */
    const texte = (cle, options = {}) => (
        <input
            type={options.type || 'text'}
            value={form.personne[cle]}
            readOnly={!personneModifiable}
            onChange={(ev) => changerPersonne(cle, ev.target.value)}
            className={options.classe || ''}
            placeholder={personneModifiable ? (options.exemple || '') : 'Non renseigné'}
            maxLength={options.maxLength}
            title={personneModifiable ? undefined : raisonPersonne}
        />
    );

    return (
        <div className="etu-fiche">

            {/* ── L'EN-TÊTE, aux couleurs du module ───────────────────────── */}
            <div className="etu-fiche-haut">
                {/* Pas de teinte dérivée ici : sur le dégradé rose, un disque coloré
                    serait du bruit. La maquette met un fond blanc translucide, et elle
                    a raison — la teinte de l'avatar (E3) sert dans la LISTE, où elle
                    distingue deux lignes voisines. */}
                {/* ⚠️ PAS un <img src={photo_url}> : la route de la photo exige le
                    jeton, et le navigateur n'en envoie aucun quand c'est LUI qui
                    demande l'image. Mesuré au lot 1.5 — les vignettes restaient
                    vides sans la moindre erreur. Voir PhotoEtudiant.js. */}
                <PhotoEtudiant
                    chemin={e.photo_url}
                    className="etu-fiche-avatar etu-fiche-avatar-photo"
                    alt=""
                    secours={<div className="etu-fiche-avatar">{initiales(e.nom, e.prenom)}</div>}
                />
                <div className="etu-fiche-nom">{e.nom}</div>
                <div className="etu-fiche-prenom">{e.prenom}</div>
                <div className="etu-fiche-code">
                    N° ÉTUDIANT · {e.code_etudiant || 'non renseigné'}
                </div>
                <div className="etu-fiche-badges">
                    {/* ⚠️ EN PREMIER, ET C'EST UN DÉFAUT TROUVÉ À L'IMAGE. Sans ce badge,
                        la fiche d'un étudiant DÉSACTIVÉ affichait encore le badge vert
                        « Actif » — et la liste, elle, barrait son nom. Les deux notions
                        sont pourtant distinctes : `statut` décrit son INSCRIPTION de
                        l'année (actif, suspendu, abandon, mobilité), `actif` décrit la
                        PERSONNE et vaut faux quand elle a quitté le département. Côte à
                        côte sans explication, c'était un mensonge à l'écran.

                        Il passe AVANT la formation : c'est le fait qui conditionne la
                        lecture de tous les autres. */}
                    {e.actif === false && (
                        <span className="etu-badge etu-badge-desactive"
                              title="Cet étudiant a été désactivé : sa fiche est conservée, sa photo a été supprimée.">
                            <i className="fa-solid fa-user-slash" /> Désactivé
                        </span>
                    )}
                    {ins?.formation_nom && (
                        <span className="etu-badge etu-badge-formation">
                            {ins.formation_nom}{ins.parcours_code ? ' · ' + ins.parcours_code : ''}
                        </span>
                    )}
                    {ins && <span className={'etu-badge ' + st.classe}>{st.libelle}</span>}
                    {ins?.boursier === true && (
                        <span className="etu-badge etu-badge-boursier">
                            <i className="fa-solid fa-euro-sign" /> Boursier
                        </span>
                    )}
                    {ins?.redoublant === true && (
                        <span className="etu-badge etu-badge-redoublant">
                            <i className="fa-solid fa-rotate-left" /> Redoublant
                        </span>
                    )}
                </div>
            </div>

            <div className="etu-fiche-corps">

                {/* ── La légende, pleine largeur, comme la maquette ───────── */}
                <div className="etu-fiche-legende">
                    <span>
                        <span className="etu-tag-apogee">APOGEE</span> importé — modifiable, et la
                        correction se verrouille <i className="fa-solid fa-lock" />
                    </span>
                    {/* Troisième entrée au lot 1.4 : un tag qui apparaît dans le corps
                        sans figurer dans la légende laisse deviner ce qu'il veut dire. */}
                    <span>
                        <span className="etu-tag-scodoc">SCODOC</span> importé — du recrutement
                        ou d'un relevé, même règle
                    </span>
                    <span>
                        <span className="etu-tag-manuel">MANUEL</span> corrigé sur cette fiche
                    </span>
                    <span>
                        <span className="etu-tag-app">APP</span> saisie ou calculée par l'application
                    </span>
                </div>

                {/* Le retour d'enregistrement : un bandeau EN LIGNE, jamais une modale
                    (parti déjà acté pour ce module, écart E19). Il nomme les champs
                    écrits et les cadenas qui ont bougé — poser un verrou est une
                    conséquence durable, elle se dit. */}
                {bandeau && (
                    <div className={'etu-fiche-bandeau etu-fiche-bandeau-' + bandeau.type}>
                        <i className={'fa-solid ' + (
                            bandeau.type === 'succes' ? 'fa-circle-check'
                                : bandeau.type === 'erreur' ? 'fa-circle-exclamation'
                                    : 'fa-circle-info')} />
                        <span>
                            {bandeau.texte}
                            {bandeau.detail && <><br />{bandeau.detail}</>}
                        </span>
                        {bandeau.rechargeable && (
                            <button type="button" className="etu-fiche-bandeau-action" onClick={charger}>
                                <i className="fa-solid fa-rotate" /> Recharger la fiche
                            </button>
                        )}
                    </div>
                )}

                {/* Lecture seule : le dire une fois, en haut, plutôt que de laisser
                    l'utilisateur découvrir champ par champ que rien ne s'enregistre. */}
                {!modifiable && (
                    <div className="etu-alerte etu-alerte-info">
                        <i className="fa-solid fa-eye" />
                        <span>
                            Fiche en <strong>lecture seule</strong> : ton profil donne le niveau
                            « consulter » sur le module Étudiants.
                        </span>
                    </div>
                )}
                {modifiable && !anneeActive && (
                    <div className="etu-alerte etu-alerte-info">
                        <i className="fa-solid fa-clock-rotate-left" />
                        <span>
                            Fiche ouverte sur <strong>{fiche.annee?.libelle}</strong>, qui n'est pas
                            l'année active. L'identité, le contact et le bac restent modifiables — ils
                            n'appartiennent à aucune année. <strong>Statut, boursier, redoublant et
                            classement</strong>, eux, vivent sur l'inscription : ils ne se modifient
                            que sur l'année active.
                        </span>
                    </div>
                )}

                {!ins && (
                    <div className="etu-alerte etu-alerte-warning">
                        <i className="fa-solid fa-triangle-exclamation" />
                        <span>
                            Aucune inscription pour <strong>{fiche.annee?.libelle}</strong>.
                            {fiche.autres_annees?.length
                                ? <> Cet étudiant est inscrit en {fiche.autres_annees
                                    .map((a) => a.libelle).join(', ')}.</>
                                : <> Formation, régime, statut et boursier appartiennent à
                                    l'inscription : sans elle, il n'y a rien à afficher.</>}
                        </span>
                    </div>
                )}

                <div className="etu-fiche-2col">

                    {/* ══ COLONNE GAUCHE ═══════════════════════════════════ */}
                    <div className="etu-fiche-col">

                        <div className="etu-fiche-section">
                            <div className="etu-fiche-section-titre">
                                <i className="fa-solid fa-circle-dot" /> Statut étudiant
                            </div>
                            {/* Statut : tag APP et AUCUN cadenas — écart E32. */}
                            <Ligne cle="statut" source="app">
                                <select
                                    value={form.inscription.statut}
                                    disabled={!inscriptionModifiable}
                                    title={inscriptionModifiable ? undefined : raisonInscription}
                                    onChange={(ev) => changerInscription('statut', ev.target.value)}
                                >
                                    <option value="">—</option>
                                    {statutsMenu.map((s) => (
                                        <option key={s.cle} value={s.cle}>{s.libelle}</option>
                                    ))}
                                    {/* Valeur héritée hors menu (E30) : elle doit rester
                                        lisible, sinon le menu afficherait « — » sur une
                                        donnée qui existe. */}
                                    {form.inscription.statut
                                        && !statutsMenu.some((s) => s.cle === form.inscription.statut) && (
                                        <option value={form.inscription.statut}>{st.libelle}</option>
                                    )}
                                </select>
                            </Ligne>
                            <Ligne
                                cle="boursier" source={src('boursier', 'apogee')}
                                verrou={verrou('boursier')} verrouActif={verrouActif('boursier')}
                                onVerrou={() => basculerVerrou('boursier')} raisonVerrou={raisonVerrou('boursier')}
                            >
                                <select
                                    value={form.inscription.boursier}
                                    disabled={!inscriptionModifiable}
                                    title={inscriptionModifiable ? undefined : raisonInscription}
                                    onChange={(ev) => changerInscription('boursier', ev.target.value)}
                                >
                                    <option value="">—</option>
                                    <option value="oui">Oui</option>
                                    <option value="non">Non</option>
                                </select>
                            </Ligne>
                            {/* Écart E30 : « redoublant » n'est plus une valeur de statut,
                                c'est un fait annuel venu d'APOGEE. Il a donc sa propre
                                ligne, verrouillable comme « boursier ». */}
                            <Ligne
                                cle="redoublant" source={src('redoublant', 'apogee')}
                                verrou={verrou('redoublant')} verrouActif={verrouActif('redoublant')}
                                onVerrou={() => basculerVerrou('redoublant')} raisonVerrou={raisonVerrou('redoublant')}
                            >
                                <select
                                    value={form.inscription.redoublant}
                                    disabled={!inscriptionModifiable}
                                    title={inscriptionModifiable ? undefined : raisonInscription}
                                    onChange={(ev) => changerInscription('redoublant', ev.target.value)}
                                >
                                    <option value="">—</option>
                                    <option value="oui">Oui</option>
                                    <option value="non">Non</option>
                                </select>
                            </Ligne>
                        </div>

                        <div className="etu-fiche-section">
                            <div className="etu-fiche-section-titre">
                                <i className="fa-solid fa-id-card" /> Identité
                            </div>
                            {/* INE : pas de cadenas, et ce n'est pas un oubli. C'est une clé,
                                `verrouillable: false` dans la liste blanche de l'import — elle
                                se corrige à la source, jamais par-dessus un import. */}
                            <Ligne cle="ine" note="clé unique">
                                <Lecture valeur={ouTiret(e.ine)} classe="etu-mono" />
                            </Ligne>
                            <Ligne
                                cle="nom" source={src('nom', 'apogee')}
                                verrou={verrou('nom')} verrouActif={verrouActif('nom')}
                                onVerrou={() => basculerVerrou('nom')} raisonVerrou={raisonVerrou('nom')}
                            >
                                {texte('nom', { maxLength: 100 })}
                            </Ligne>
                            <Ligne
                                cle="prenom" source={src('prenom', 'apogee')}
                                verrou={verrou('prenom')} verrouActif={verrouActif('prenom')}
                                onVerrou={() => basculerVerrou('prenom')} raisonVerrou={raisonVerrou('prenom')}
                            >
                                {texte('prenom', { maxLength: 100 })}
                            </Ligne>
                            {/* Écart E38 : la maquette dessine un champ texte valant
                                « Féminin ». La colonne fait UN caractère — ce texte n'y
                                entrerait pas. Menu à deux valeurs, plus « — » pour l'inconnu. */}
                            <Ligne
                                cle="sexe" source={src('sexe', 'apogee')}
                                verrou={verrou('sexe')} verrouActif={verrouActif('sexe')}
                                onVerrou={() => basculerVerrou('sexe')} raisonVerrou={raisonVerrou('sexe')}
                            >
                                <select
                                    value={form.personne.sexe}
                                    disabled={!personneModifiable}
                                    title={personneModifiable ? undefined : raisonPersonne}
                                    onChange={(ev) => changerPersonne('sexe', ev.target.value)}
                                >
                                    <option value="">—</option>
                                    <option value="M">Masculin</option>
                                    <option value="F">Féminin</option>
                                </select>
                            </Ligne>
                            {/* Écart E38 : un calendrier plutôt qu'une chaîne à relire. */}
                            <Ligne
                                cle="date_naissance" source={src('date_naissance', 'apogee')}
                                verrou={verrou('date_naissance')} verrouActif={verrouActif('date_naissance')}
                                onVerrou={() => basculerVerrou('date_naissance')}
                                raisonVerrou={raisonVerrou('date_naissance')}
                            >
                                {personneModifiable
                                    ? texte('date_naissance', { type: 'date' })
                                    : <Lecture valeur={dateFr(e.date_naissance)} />}
                            </Ligne>
                        </div>

                        <div className="etu-fiche-section">
                            <div className="etu-fiche-section-titre">
                                <i className="fa-solid fa-award" /> Bac &amp; cursus
                            </div>
                            {/* Trois lignes et trois cadenas — écart E31. Les en-têtes du
                                fichier APOGEE ne décrivent pas leur contenu (§0.3) : la
                                mention vient de « Bac - Etab. préparation », l'année de
                                « Bac - Département obtention ». Mesuré, pas supposé. */}
                            <Ligne
                                cle="type_bac" source={src('type_bac', 'apogee')}
                                verrou={verrou('type_bac')} verrouActif={verrouActif('type_bac')}
                                onVerrou={() => basculerVerrou('type_bac')} raisonVerrou={raisonVerrou('type_bac')}
                            >
                                {texte('type_bac', { maxLength: 50 })}
                            </Ligne>
                            {/* Écart E38 : quatre valeurs mesurées 146/146, donc un menu.
                                Une saisie libre n'aurait produit que des refus. */}
                            <Ligne
                                cle="mention_bac" source={src('mention_bac', 'apogee')}
                                verrou={verrou('mention_bac')} verrouActif={verrouActif('mention_bac')}
                                onVerrou={() => basculerVerrou('mention_bac')} raisonVerrou={raisonVerrou('mention_bac')}
                            >
                                <select
                                    value={form.personne.mention_bac}
                                    disabled={!personneModifiable}
                                    title={personneModifiable ? undefined : raisonPersonne}
                                    onChange={(ev) => changerPersonne('mention_bac', ev.target.value)}
                                >
                                    <option value="">—</option>
                                    <option value="P">P — Passable</option>
                                    <option value="AB">AB — Assez bien</option>
                                    <option value="B">B — Bien</option>
                                    <option value="TB">TB — Très bien</option>
                                </select>
                            </Ligne>
                            <Ligne
                                cle="annee_bac" source={src('annee_bac', 'apogee')}
                                verrou={verrou('annee_bac')} verrouActif={verrouActif('annee_bac')}
                                onVerrou={() => basculerVerrou('annee_bac')} raisonVerrou={raisonVerrou('annee_bac')}
                            >
                                {texte('annee_bac', { maxLength: 4, exemple: '2025' })}
                            </Ligne>
                            <Ligne
                                cle="cursus" source={src('cursus', 'apogee')}
                                verrou={verrou('cursus')} verrouActif={verrouActif('cursus')}
                                onVerrou={() => basculerVerrou('cursus')} raisonVerrou={raisonVerrou('cursus')}
                            >
                                {texte('cursus', { classe: 'etu-fiche-large', maxLength: 2000 })}
                            </Ligne>
                        </div>

                    </div>

                    {/* ══ COLONNE DROITE ═══════════════════════════════════ */}
                    <div className="etu-fiche-col">

                        <div className="etu-fiche-section">
                            <div className="etu-fiche-section-titre">
                                <i className="fa-solid fa-envelope" /> Contact
                            </div>
                            <Ligne
                                cle="email" source={src('email', 'apogee')}
                                verrou={verrou('email')} verrouActif={verrouActif('email')}
                                onVerrou={() => basculerVerrou('email')} raisonVerrou={raisonVerrou('email')}
                            >
                                {texte('email', { classe: 'etu-fiche-mail', maxLength: 255 })}
                            </Ligne>
                            {/* Généré, jamais saisi : `{numéro}@{domaine}`, recalculé à
                                l'import si le domaine change dans le Paramétrage. */}
                            <Ligne cle="email_institutionnel" source="app">
                                <Lecture valeur={e.email_institutionnel} classe="etu-fiche-instit" />
                            </Ligne>
                            <Ligne
                                cle="telephone" source={src('telephone', 'apogee')}
                                verrou={verrou('telephone')} verrouActif={verrouActif('telephone')}
                                onVerrou={() => basculerVerrou('telephone')} raisonVerrou={raisonVerrou('telephone')}
                            >
                                {texte('telephone', { maxLength: 20, exemple: '06 12 34 56 78' })}
                            </Ligne>
                            {/* Ni « Adresse » ni « Fixe » : écart E21. La maquette les montre
                                avec le tag APOGEE, la v38 ne crée pas les colonnes, et le
                                §0.6 explique pourquoi elles n'entreront pas. */}
                        </div>

                        <div className="etu-fiche-section">
                            <div className="etu-fiche-section-titre">
                                <i className="fa-solid fa-graduation-cap" /> Suivi pédagogique
                            </div>
                            {/* 2026-10-22 (demande de Bertrand) : formation et régime viennent du mapping APOGEE,
                                et se CORRIGENT ici — leur cadenas protège la correction du prochain import, pour
                                l'année de la fiche. La fenêtre de confirmation dit ce que ça change ailleurs. */}
                            <Ligne
                                cle="sous_niveau_id" source={src('sous_niveau_id', 'apogee')}
                                verrou={verrou('sous_niveau_id')} verrouActif={verrouActif('sous_niveau_id')}
                                onVerrou={() => basculerVerrou('sous_niveau_id')} raisonVerrou={raisonVerrou('sous_niveau_id')}
                            >
                                <select
                                    className="etu-fiche-formation"
                                    value={form.inscription.sous_niveau_id}
                                    disabled={!inscriptionModifiable}
                                    title={inscriptionModifiable ? undefined : raisonInscription}
                                    onChange={(ev) => changerInscription('sous_niveau_id', ev.target.value)}
                                >
                                    {!form.inscription.sous_niveau_id && <option value="">—</option>}
                                    {(fiche.formations || []).map((f) => <option key={f.id} value={String(f.id)}>{f.nom}</option>)}
                                    {/* Une formation devenue inactive reste lisible (elle n'est plus proposée). */}
                                    {form.inscription.sous_niveau_id
                                        && !(fiche.formations || []).some((f) => String(f.id) === form.inscription.sous_niveau_id) && (
                                        <option value={form.inscription.sous_niveau_id}>{ouTiret(ins?.formation_nom)} (inactive)</option>
                                    )}
                                </select>
                            </Ligne>
                            {/* 2026-10-29 (E.4, demande de Bertrand) : le PARCOURS de l'année, sous la formation — posé par
                                l'import (mapping Apogée ou SCODOC), corrigeable ici ; vide = tronc commun. Les parcours
                                proposés sont ceux du Programme pour l'année de la fiche. */}
                            <Ligne
                                cle="parcours_pn_id" source={src('parcours_pn_id')}
                                verrou={verrou('parcours_pn_id')} verrouActif={verrouActif('parcours_pn_id')}
                                onVerrou={() => basculerVerrou('parcours_pn_id')} raisonVerrou={raisonVerrou('parcours_pn_id')}
                            >
                                <select
                                    className="etu-fiche-formation"
                                    value={form.inscription.parcours_pn_id}
                                    disabled={!inscriptionModifiable}
                                    title={inscriptionModifiable ? undefined : raisonInscription}
                                    onChange={(ev) => changerInscription('parcours_pn_id', ev.target.value)}
                                >
                                    <option value="">— Tronc commun / aucun</option>
                                    {(fiche.parcours || []).map((p) => <option key={p.id} value={String(p.id)}>{p.nom && p.nom !== p.code ? p.code + ' — ' + p.nom : p.code}</option>)}
                                    {form.inscription.parcours_pn_id
                                        && !(fiche.parcours || []).some((p) => String(p.id) === form.inscription.parcours_pn_id) && (
                                        <option value={form.inscription.parcours_pn_id}>{ouTiret(ins?.parcours_code)}</option>
                                    )}
                                </select>
                            </Ligne>
                            <Ligne
                                cle="regime_inscription" source={src('regime_inscription', 'apogee')}
                                verrou={verrou('regime_inscription')} verrouActif={verrouActif('regime_inscription')}
                                onVerrou={() => basculerVerrou('regime_inscription')} raisonVerrou={raisonVerrou('regime_inscription')}
                            >
                                <select
                                    className="etu-fiche-regime"
                                    value={form.inscription.regime_inscription}
                                    disabled={!inscriptionModifiable}
                                    title={inscriptionModifiable ? undefined : raisonInscription}
                                    onChange={(ev) => changerInscription('regime_inscription', ev.target.value)}
                                >
                                    {!form.inscription.regime_inscription && <option value="">—</option>}
                                    {(fiche.regimes || []).map((r) => <option key={r.cle} value={r.cle}>{r.libelle}</option>)}
                                </select>
                                <span className="etu-fiche-etape">
                                    {ins?.code_etape ? `${ins.code_etape} / ${ins.version_etape}` : ''}
                                </span>
                            </Ligne>
                            {/* Le « 1 / 142 » de la maquette : l'effectif est un COUNT par
                                formation et par année (§0.5), jamais une colonne. */}
                            <Ligne
                                cle="classement_admission" source={src('classement_admission', 'apogee')}
                                verrou={verrou('classement_admission')}
                                verrouActif={verrouActif('classement_admission')}
                                onVerrou={() => basculerVerrou('classement_admission')}
                                raisonVerrou={raisonVerrou('classement_admission')}
                            >
                                <input
                                    type="number"
                                    min="1"
                                    max="999"
                                    className="etu-fiche-rang"
                                    value={form.inscription.classement_admission}
                                    readOnly={!inscriptionModifiable}
                                    title={inscriptionModifiable ? undefined : raisonInscription}
                                    onChange={(ev) => changerInscription('classement_admission', ev.target.value)}
                                />
                                <span className="etu-fiche-effectif">
                                    / {fiche.effectif_promo ?? '—'}
                                </span>
                            </Ligne>
                            {/* ── LE RANG À L'ADMISSION — lot 1.4 ──
                                Le second classement, et le seul champ de la fiche que
                                SCODOC alimente. Il est ici et non fondu avec le
                                précédent parce que les deux nombres ne comptent pas la
                                même population : APOGEE classe la promotion INSCRITE
                                (1 → 66), SCODOC tous les candidats CLASSÉS (11 → 210).
                                Mesuré : sur les 48 lignes qui portent les deux, 47
                                diffèrent (§0.5).

                                Le dénominateur affiché à côté n'est PAS l'effectif de
                                la promotion mais `rang_max_recrutement` : le rang le
                                plus haut de la campagne. Afficher « 34 / 142 » avec un
                                rang qui monte à 210 serait faux à l'œil. */}
                            <Ligne
                                cle="classement_recrutement" source={src('classement_recrutement', 'scodoc')}
                                verrou={verrou('classement_recrutement')}
                                verrouActif={verrouActif('classement_recrutement')}
                                onVerrou={() => basculerVerrou('classement_recrutement')}
                                raisonVerrou={raisonVerrou('classement_recrutement')}
                            >
                                <input
                                    type="number"
                                    min="1"
                                    max="9999"
                                    className="etu-fiche-rang"
                                    value={form.inscription.classement_recrutement}
                                    readOnly={!inscriptionModifiable}
                                    title={inscriptionModifiable ? undefined : raisonInscription}
                                    onChange={(ev) => changerInscription('classement_recrutement', ev.target.value)}
                                />
                                <span className="etu-fiche-effectif">
                                    / {ins?.rang_max_recrutement ?? '—'}
                                </span>
                            </Ligne>
                            {/* ── LE RANG DANS LA PROMOTION — lot 1.4 ──
                                Le TROISIÈME rang de la fiche, et il ne se confond avec
                                aucun des deux autres. Mesuré sur les fichiers réels :
                                  · « Classement »     APOGEE, promotion inscrite, 1 → 66
                                  · « Rang admission » SCODOC, candidats classés, 11 → 210
                                  · « Rang promo »     SCODOC, promotion au SEMESTRE, 1 → 41
                                Un rang qui s'arrête à 41 sur 41 étudiants classe une
                                promotion ; un rang qui monte à 210 classe des candidats.

                                ⚠️ Le SEMESTRE est affiché à côté, et ce n'est pas
                                décoratif : ce rang change à chaque semestre, et « 12 / 41 »
                                sans sa date est un nombre que personne ne peut relire.

                                ⚠️ Et il n'est modifiable QU'APRÈS l'import de son relevé :
                                le semestre vient du relevé, et la base refuse un rang sans
                                semestre (contrainte `ei_rang_promotion_date`, v39). Sans
                                cette règle, saisir ici ferait remonter une violation de
                                contrainte PostgreSQL — le message d'un moteur de base de
                                données, illisible et inactionnable. */}
                            <Ligne
                                cle="rang_promotion" source={src('rang_promotion', 'scodoc')}
                                note={ins?.rang_promotion_semestre
                                    ? 'semestre ' + ins.rang_promotion_semestre : null}
                                verrou={verrou('rang_promotion')}
                                verrouActif={verrouActif('rang_promotion')}
                                onVerrou={() => basculerVerrou('rang_promotion')}
                                raisonVerrou={raisonVerrou('rang_promotion')}
                            >
                                <input
                                    type="number"
                                    min="1"
                                    max="999"
                                    className="etu-fiche-rang"
                                    value={form.inscription.rang_promotion}
                                    readOnly={!rangPromoModifiable}
                                    title={rangPromoModifiable ? undefined : raisonRangPromo}
                                    onChange={(ev) => changerInscription('rang_promotion', ev.target.value)}
                                />
                                <span className="etu-fiche-effectif">
                                    / {ins?.rang_promotion_max ?? '—'}
                                </span>
                            </Ligne>
                            <Ligne cle="enseignant_referent_id" source="app">
                                <select
                                    value={form.personne.enseignant_referent_id}
                                    disabled={!personneModifiable}
                                    title={personneModifiable ? undefined : raisonPersonne}
                                    onChange={(ev) => changerPersonne('enseignant_referent_id', ev.target.value)}
                                >
                                    <option value="">— Aucun enseignant référent</option>
                                    {(fiche.enseignants || []).map((x) => (
                                        <option key={x.id} value={x.id}>{x.nom} {x.prenom}</option>
                                    ))}
                                    {form.personne.enseignant_referent_id && !referent && (
                                        <option value={form.personne.enseignant_referent_id}>
                                            Enseignant retiré du référentiel
                                        </option>
                                    )}
                                </select>
                            </Ligne>
                        </div>

                    </div>
                </div>
            </div>

            {/* ── LES DEUX ACTIONS DE LA MAQUETTE ─────────────────────────── */}
            <div className="etu-fiche-actions">
                <button
                    className="etu-btn-fiche etu-btn-fiche-primaire"
                    type="button"
                    disabled={!modifiable || !modifie || enregistrement || lectureConsequences}
                    onClick={demanderEnregistrement}
                    title={
                        !modifiable ? raisonPersonne
                            : !modifie ? "Rien n'a changé sur cette fiche."
                                : "Enregistrer les modifications et les cadenas."
                    }
                >
                    {enregistrement
                        ? <><i className="fa-solid fa-circle-notch etu-tourne" /> Enregistrement…</>
                        : <><i className="fa-solid fa-check" /> Enregistrer
                            {modifie && <span className="etu-fiche-pastille" aria-hidden="true" />}</>}
                </button>
                <button
                    className="etu-btn-fiche etu-btn-fiche-secondaire"
                    type="button"
                    disabled={!adresseContact}
                    onClick={() => { window.location.href = 'mailto:' + adresseContact; }}
                    title={adresseContact
                        ? `Écrire à ${adresseContact}`
                        : "Aucune adresse e-mail sur cette fiche."}
                >
                    <i className="fa-solid fa-envelope" /> Contacter
                </button>

                {/* ── LE TROISIÈME BOUTON, absent de la maquette (écart E9) ────
                    Il est à DROITE et discret : c'est une action rare, et la mettre
                    à côté d'« Enregistrer » invitait au clic de trop. Sa couleur ne
                    crie pas non plus — désactiver n'est pas une erreur, c'est une
                    décision de fin de parcours. */}
                <button
                    className={'etu-btn-fiche etu-btn-fiche-etat'
                        + (e.actif === false ? ' etu-btn-fiche-reactiver' : '')}
                    type="button"
                    disabled={!modifiable || bascule}
                    onClick={() => setDemandeActif(e.actif === false)}
                    title={!modifiable ? raisonPersonne
                        : e.actif === false
                            ? 'Réactiver cet étudiant. Sa photo n’est pas restaurée.'
                            : 'Désactiver cet étudiant. Sa photo sera supprimée.'}
                >
                    {bascule
                        ? <><i className="fa-solid fa-circle-notch etu-tourne" /> …</>
                        : e.actif === false
                            ? <><i className="fa-solid fa-rotate-left" /> Réactiver</>
                            : <><i className="fa-solid fa-user-slash" /> Désactiver</>}
                </button>
            </div>

            {/* ── LA MODALE : elle NOMME la conséquence avant de la produire ──
                Jamais un `confirm()` (règle 6). Et le texte diffère dans les deux
                sens, parce que les deux gestes ne sont pas symétriques : l'un
                supprime une photo, l'autre ne la rend pas. */}
            {confirmation && (
                <div className="etu-modale-fond" role="dialog" aria-modal="true" aria-labelledby="etu-modale-ins-titre">
                    <div className="etu-modale etu-modale-inscription">
                        <div className="etu-modale-entete">
                            <i className="fa-solid fa-graduation-cap" />
                            <h3 id="etu-modale-ins-titre">Modifier l'inscription de {e.nom} {e.prenom} ?</h3>
                        </div>
                        <div className="etu-modale-corps">
                            <table className="etu-modale-avant-apres">
                                <tbody>
                                    {confirmation.avant.formation !== confirmation.apres.formation && (
                                        <tr><th>Formation</th><td>{ouTiret(confirmation.avant.formation)}</td><td>→</td><td><strong>{ouTiret(confirmation.apres.formation)}</strong></td></tr>
                                    )}
                                    {confirmation.avant.regime !== confirmation.apres.regime && (
                                        <tr><th>Régime</th><td>{badgeRegime(confirmation.avant.regime).libelle}</td><td>→</td><td><strong>{badgeRegime(confirmation.apres.regime).libelle}</strong></td></tr>
                                    )}
                                </tbody>
                            </table>
                            <ul className="etu-modale-consequences">
                                {confirmation.messages.map((m, k) => (
                                    <li key={k} className={'etu-csq-' + m.ton}>
                                        <i className={'fa-solid ' + (m.ton === 'alerte' ? 'fa-triangle-exclamation' : m.ton === 'info' ? 'fa-circle-info' : 'fa-lock')} /> {m.texte}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="etu-modale-pied">
                            <button type="button" className="etu-btn-modale etu-btn-modale-primaire" disabled={enregistrement}
                                    onClick={async () => { const ok = await enregistrer(); if (ok) setConfirmation(null); }}>
                                <i className="fa-solid fa-check" /> Enregistrer
                            </button>
                            <button type="button" className="etu-btn-modale etu-btn-modale-secondaire" disabled={enregistrement}
                                    onClick={() => setConfirmation(null)}>
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {demandeActif !== null && (
                <div className="etu-modale-fond" role="dialog" aria-modal="true"
                     aria-labelledby="etu-modale-etat-titre">
                    <div className="etu-modale">
                        <div className="etu-modale-entete">
                            <i className={'fa-solid '
                                + (demandeActif ? 'fa-rotate-left' : 'fa-user-slash')} />
                            <h3 id="etu-modale-etat-titre">
                                {demandeActif ? 'Réactiver' : 'Désactiver'} {e.nom} {e.prenom} ?
                            </h3>
                        </div>
                        <div className="etu-modale-corps">
                            {demandeActif ? (
                                <>
                                    <p>
                                        Cet étudiant redeviendra actif et réapparaîtra normalement
                                        dans la liste et le trombinoscope.
                                    </p>
                                    <p className="etu-modale-note">
                                        ⚠️ Sa photo <strong>n’est pas restaurée</strong> : elle a été
                                        supprimée à la désactivation, et cette suppression est
                                        définitive. Il faudra la réimporter.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p>
                                        Il ne sera pas supprimé : son identité, ses inscriptions et
                                        l’historique des imports restent en base. Il sera marqué
                                        comme parti.
                                    </p>
                                    <p className="etu-modale-note">
                                        ⚠️ <strong>Sa photo sera supprimée</strong>, et cette
                                        suppression est définitive — une photographie n’a plus de
                                        finalité une fois l’étudiant parti. Le reste de la fiche est
                                        conservé.
                                    </p>
                                </>
                            )}
                        </div>
                        <div className="etu-modale-pied">
                            <button
                                type="button"
                                className={'etu-btn-modale '
                                    + (demandeActif ? 'etu-btn-modale-primaire' : 'etu-btn-modale-danger')}
                                disabled={bascule}
                                onClick={() => changerActif(demandeActif)}
                            >
                                <i className={'fa-solid '
                                    + (demandeActif ? 'fa-rotate-left' : 'fa-user-slash')} />
                                {' '}{demandeActif ? 'Réactiver' : 'Désactiver et supprimer la photo'}
                            </button>
                            <button
                                type="button"
                                className="etu-btn-modale etu-btn-modale-secondaire"
                                disabled={bascule}
                                onClick={() => setDemandeActif(null)}
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EtudiantFiche;
